// useCompanyList.js
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { get, del, put } from "@utils/axios_api";
import { toast } from 'react-toastify';

const fetchCompanyList = async (params) => {
  const response = await get("/admin/corp/corplist", { params });
  return response;
};

// (새 함수) 실제 삭제 API 호출
async function fetchDeleteCompanies(compCodes) {
  // DELETE /api/admin/corp/deleteCorp?compCodes=AAA,BBB,CCC
  const paramString = compCodes.join(",");
  const url = `/admin/corp/deleteCorp?compCodes=${paramString}`;
  const response = await put(url);
  return response;
}

export function useCompanyList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedItems, setSelectedItems] = useState([]);
  const queryClient = useQueryClient();

  const methods = useForm({
    defaultValues: {
      searchCnd: "",
      searchWrd: "",
      createStDate: "",
      createEndDate: "",
    },
    shouldFocusError: false,
  });

  const { watch, setValue } = methods;

  const searchCnd = searchParams.get("searchCnd") || "";
  const searchWrd = searchParams.get("searchWrd") || "";
  const createStDate = searchParams.get("createStDate") || "";
  const createEndDate = searchParams.get("createEndDate") || "";
  const corppageIndex = parseInt(searchParams.get("corppageIndex") || "1", 10);
  const recordCountPerPage = parseInt(searchParams.get("corpPageSize") || "10", 10);
  const sortField = searchParams.get("sortField") || "";
  const sortOrder = searchParams.get("sortOrder") || "";

  const queryParam = {
    searchCnd,
    searchWrd,
    createStDate,
    createEndDate,
    pageIndex: corppageIndex,
    recordCountPerPage,
    sortField,
    sortOrder,
  };

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["selectCompanyList", queryParam],
    queryFn: () => fetchCompanyList(queryParam),
    keepPreviousData: true,
    staleTime: 600000,
  });

  const refreshList = () => {
    refetch();
  };

  // 검색 폼 submit
  const onSubmit = (values) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("searchCnd", values.searchCnd || "");
    newParams.set("searchWrd", values.searchWrd || "");
    newParams.set("createStDate", values.createStDate ? values.createStDate + " 00:00:00" : "");
    newParams.set("createEndDate", values.createEndDate ? values.createEndDate + " 23:59:59" : "");
    newParams.set("corppageIndex", "1");
    setSearchParams(newParams);
  };

  // 페이지 변경
  const handlePageChange = (targetPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("corppageIndex", targetPage);
    setSearchParams(newParams);
  };

  const changePageSize = (size) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("corpPageSize", String(size));
    newParams.set("corppageIndex", "1");
    setSearchParams(newParams);
  };

  // 전체 선택
  const handleSelectAll = (e) => {
    if (!data || !data.resultList) return;
    const { checked } = e.target;
    if (checked) {
      const allIds = data.resultList.map((item) => item.compCode);
      setSelectedItems(allIds);
    } else {
      setSelectedItems([]);
    }
  };

  // 단일 선택
  const handleSelectItem = (compCode) => {
    if (selectedItems.includes(compCode)) {
      setSelectedItems(selectedItems.filter((id) => id !== compCode));
    } else {
      setSelectedItems([...selectedItems, compCode]);
    }
  };

  // (수정) handleDelete: 이벤트 파라미터 없음, API 호출
  const handleDelete = async () => {
    if (selectedItems.length === 0) {
      toast.warn("삭제할 항목을 선택해주세요.");
      return;
    }
    try {
      const res = await fetchDeleteCompanies(selectedItems);
      if (res.status === false) {
        toast.error(res.message || "삭제 실패");
      } else {
        toast.success(res.message || "삭제 성공");
        refreshList();
        queryClient.invalidateQueries({ queryKey: ["selectSiteList"] });
        setSelectedItems([]);
      }
    } catch (err) {
      console.error("삭제 오류:", err);
    }
  };

  const isAllChecked =
    data?.resultList &&
    data.resultList.length > 0 &&
    selectedItems.length === data.resultList.length;

  return {
    methods,
    data,
    isLoading,
    isError,
    error,
    watch,
    setValue,
    selectedItems,
    isAllChecked,
    onSubmit,
    handlePageChange,
    changePageSize,
    handleSelectAll,
    handleSelectItem,
    handleDelete,
    corppageIndex,
    recordCountPerPage,
    refreshList,
  };
}
